import { useWatch } from 'react-hook-form'

import { Money } from '@/components/ds'
import { QrCode } from '@/components/QrCode'
import { Settings } from '@/interfaces/Settings'
import { components } from '@invo.space/shared'

import { calculateTotals, toNumber } from '../calculateTotals'
import type { DocumentFormValues } from '../DocumentForm'

type BankAccount = components['schemas']['InvoicingBankAccount']

const titleByType = { invoice: 'Faktura', order: 'Výzva k platbě', expense: 'Přijatý doklad' } as const

const paymentLabel: Record<string, string> = {
  bankTransfer: 'Bankovní převod',
  card: 'Kartou',
  cash: 'Hotově',
  cod: 'Dobírka',
  other: 'Jiný',
}

interface PreviewParty {
  name?: string | null
  businessId?: string | null
  taxId?: string | null
  email?: string | null
  address?: { address?: string; city?: string; zip?: string; country?: string | null } | null
}

const Party = ({ label, party }: { label: string; party?: PreviewParty | null }) => (
  <div className="min-w-0 space-y-0.5">
    <p className="text-[9px] font-medium uppercase tracking-[0.08em] text-muted-foreground">{label}</p>
    {party?.name ? (
      <>
        <p className="truncate text-[11px] font-medium">{party.name}</p>
        {party.address?.address && <p className="truncate">{party.address.address}</p>}
        {(party.address?.zip || party.address?.city) && (
          <p className="truncate">
            {party.address?.zip} {party.address?.city}
          </p>
        )}
        {party.businessId && <p>IČO {party.businessId}</p>}
        {party.taxId && <p>DIČ {party.taxId}</p>}
      </>
    ) : (
      <p className="text-muted-foreground">Doplňte údaje</p>
    )}
  </div>
)

/** Živý náhled dokladu ve formátu A4 vedle formuláře (Acctual/Kitchen.co vzor). */
export const DocumentPreview = ({
  type,
  settings,
  bankAccounts,
  documentNumber,
}: {
  documentNumber?: string | null
  type: 'invoice' | 'order' | 'expense'
  settings: Settings
  bankAccounts?: BankAccount[]
}) => {
  const values = useWatch() as Partial<DocumentFormValues>
  const items = (values.items ?? []) as DocumentFormValues['items']
  const s = (values.settings ?? {}) as DocumentFormValues['settings']
  const currency = s.currency || 'CZK'
  const totals = calculateTotals(items)
  const isVatPayer = settings.vatPayer === 'vatPayer'

  const supplierFromSettings = (settings as unknown as { participant?: { name?: string; ico?: string; dic?: string; email?: string; address?: PreviewParty['address'] } }).participant
  const ownCompany: PreviewParty | undefined = supplierFromSettings
    ? { name: supplierFromSettings.name, businessId: supplierFromSettings.ico, taxId: supplierFromSettings.dic, email: supplierFromSettings.email, address: supplierFromSettings.address }
    : undefined
  const from = type === 'expense' ? (values.supplier as PreviewParty) : ownCompany
  const to = type === 'expense' ? ownCompany : (values.customer as PreviewParty)

  const account = bankAccounts?.find((a) => a.id === s.bankAccountId) ?? bankAccounts?.find((a) => a.isDefault)
  const iban = account?.iban?.replace(/\s/g, '')
  const showQr = type !== 'expense' && s.paymentMethod !== 'cash' && !!iban && totals.total > 0
  const spayd = showQr
    ? `SPD*1.0*ACC:${iban}*AM:${(isVatPayer ? totals.total : totals.totalWithoutTaxes).toFixed(2)}*CC:${currency}${s.variableSymbol ? `*X-VS:${s.variableSymbol}` : ''}`
    : ''
  const grandTotal = isVatPayer ? totals.total : totals.totalWithoutTaxes

  return (
    <div className="aspect-[1/1.414] w-full overflow-hidden rounded-lg border bg-background p-7 text-[10px] leading-relaxed text-foreground">
      <div className="flex items-start justify-between gap-4 border-b pb-4">
        <div>
          <p className="text-[15px] font-medium tracking-[-0.01em]">{titleByType[type]}</p>
          <p className="text-muted-foreground">{documentNumber || values.settings?.externalId || 'Číslo se přidělí při uložení'}</p>
        </div>
        <div className="grid grid-cols-[auto_auto] gap-x-3 text-right">
          <span className="text-muted-foreground">Vystaveno</span>
          <span className="tabular">{s.issueDate || '-'}</span>
          {s.taxDate && isVatPayer && (
            <>
              <span className="text-muted-foreground">DUZP</span>
              <span className="tabular">{s.taxDate}</span>
            </>
          )}
          <span className="text-muted-foreground">Splatnost</span>
          <span className="tabular">{s.dueDate || '-'}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6 border-b py-4">
        <Party label="Dodavatel" party={from} />
        <Party label="Odběratel" party={to} />
      </div>

      {values.description && <p className="border-b py-3">{values.description}</p>}

      <table className="mt-3 w-full">
        <thead>
          <tr className="text-left text-[9px] uppercase tracking-[0.06em] text-muted-foreground">
            <th className="pb-1.5 font-medium">Položka</th>
            <th className="pb-1.5 pl-2 text-right font-medium whitespace-nowrap">Množství</th>
            <th className="pb-1.5 pl-2 text-right font-medium whitespace-nowrap">Cena</th>
            {isVatPayer && <th className="pb-1.5 pl-2 text-right font-medium whitespace-nowrap">DPH</th>}
            <th className="pb-1.5 pl-2 text-right font-medium whitespace-nowrap">Celkem</th>
          </tr>
        </thead>
        <tbody>
          {items.filter((i) => i.description || i.unitPrice).map((item, i) => (
            <tr key={i} className="border-t align-top">
              <td className="w-full py-1.5 pr-2 [overflow-wrap:anywhere]">{item.description || 'Bez popisu'}</td>
              <td className="py-1.5 pl-2 text-right tabular whitespace-nowrap">
                {toNumber(item.amount).toLocaleString('cs-CZ', { maximumFractionDigits: 4 })} {item.unit}
              </td>
              <td className="py-1.5 pl-2 text-right tabular whitespace-nowrap">
                <Money amount={toNumber(item.unitPrice)} minor={false} currency={currency} size="sm" className="text-[10px] font-normal" />
              </td>
              {isVatPayer && <td className="py-1.5 pl-2 text-right tabular whitespace-nowrap">{toNumber(item.taxRate)} %</td>}
              <td className="py-1.5 pl-2 text-right tabular whitespace-nowrap">
                <Money amount={toNumber(item.amount) * toNumber(item.unitPrice)} minor={false} currency={currency} size="sm" className="text-[10px]" />
              </td>
            </tr>
          ))}
          {items.filter((i) => i.description || i.unitPrice).length === 0 && (
            <tr className="border-t">
              <td colSpan={5} className="py-3 text-muted-foreground">Přidejte první položku</td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="mt-4 flex items-end justify-between gap-4 border-t pt-4">
        <div className="space-y-1 text-muted-foreground">
          <p>{paymentLabel[s.paymentMethod ?? 'bankTransfer']}</p>
          {account && type !== 'expense' && (
            <p className="tabular">
              {account.accountNumber}/{account.bankCode}
            </p>
          )}
          {s.variableSymbol && <p className="tabular">VS {s.variableSymbol}</p>}
          {showQr && (
            <div className="pt-1 [&_img]:size-[72px]">
              <QrCode key={spayd} spaydString={spayd} />
            </div>
          )}
        </div>
        <div className="min-w-[45%] space-y-1">
          {isVatPayer && (
            <>
              <div className="flex justify-between text-muted-foreground">
                <span>Základ</span>
                <Money amount={totals.totalWithoutTaxes} minor={false} currency={currency} size="sm" className="text-[10px] font-normal" />
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>DPH</span>
                <Money amount={totals.tax} minor={false} currency={currency} size="sm" className="text-[10px] font-normal" />
              </div>
            </>
          )}
          <div className="flex items-baseline justify-between border-t pt-1.5">
            <span className="font-medium">K úhradě</span>
            <Money amount={grandTotal} minor={false} currency={currency} size="md" />
          </div>
        </div>
      </div>
    </div>
  )
}
